import { useState, useEffect } from 'react';
import { ClipboardCheck, Plus, Filter, Calendar } from 'lucide-react';
import Layout from '../components/shared/Layout';
import InspectionCard from '../components/inspections/InspectionCard';
import InspectionForm from '../components/inspections/InspectionForm';

interface Inspection {
    id: string;
    inspectionType: string;
    inspectionDate: string;
    nextDueDate: string;
    status: 'PASSED' | 'FAILED' | 'ACTION_REQUIRED';
    notes?: string;
    equipmentNumber: string;
    equipmentType: string;
    facilityName: string;
}

export default function InspectionsPage() {
    const [inspections, setInspections] = useState<Inspection[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);

    // Filters
    const [filterStatus, setFilterStatus] = useState('ALL');

    useEffect(() => {
        fetchInspections();
    }, []);

    const fetchInspections = async () => {
        try {
            const res = await fetch('https://fire-safety-api.tedguy280.workers.dev/api/inspections', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await res.json();
            if (data.success) {
                setInspections(data.inspections);
            }
        } catch (error) {
            console.error('Failed to fetch inspections:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = () => {
        setShowForm(false);
        fetchInspections(); // Refresh list
    };

    const filteredInspections = inspections.filter(i => {
        if (filterStatus === 'ALL') return true;
        return i.status === filterStatus;
    });

    return (
        <Layout>
            <div className="min-h-screen p-8">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 animate-fadeIn">
                    <div>
                        <h1 className="text-display-1 font-bold text-charcoal-950 mb-2 flex items-center gap-3">
                            <ClipboardCheck className="w-10 h-10 text-ember-500" />
                            Inspections Log
                        </h1>
                        <p className="text-steel-600">
                            Track verification history and upcoming maintenance schedules.
                        </p>
                    </div>
                    <button
                        onClick={() => setShowForm(true)}
                        className="btn btn-primary flex items-center gap-2 shadow-lg shadow-ember-500/20 group"
                    >
                        <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                        Log New Inspection
                    </button>
                </div>

                {/* Filters */}
                <div className="card-glass p-4 mb-6 flex items-center gap-4 animate-slideUp">
                    <Filter className="w-5 h-5 text-steel-500" />
                    <select
                        className="input max-w-[200px]"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value="ALL">All Statuses</option>
                        <option value="PASSED">Passed</option>
                        <option value="ACTION_REQUIRED">Action Required</option>
                        <option value="FAILED">Failed</option>
                    </select>
                    <div className="h-6 w-px bg-steel-700 mx-2" />
                    <span className="text-steel-500 text-sm">
                        Showing {filteredInspections.length} records
                    </span>
                </div>

                {/* List */}
                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="spinner w-12 h-12 border-4 text-ember-500" />
                    </div>
                ) : filteredInspections.length === 0 ? (
                    <div className="card-glass p-12 text-center animate-scaleIn">
                        <ClipboardCheck className="w-16 h-16 text-steel-400 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-white mb-2">No Inspections Found</h3>
                        <p className="text-steel-400 mb-6">Log your first equipment check to get started.</p>
                        <button onClick={() => setShowForm(true)} className="btn btn-primary">
                            Log Inspection
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredInspections.map((inspection, idx) => (
                            <div key={inspection.id} className="animate-slideUp" style={{ animationDelay: `${idx * 50}ms` }}>
                                <InspectionCard inspection={inspection} />
                            </div>
                        ))}
                    </div>
                )}

                {/* Modal */}
                {showForm && (
                    <InspectionForm
                        onClose={() => setShowForm(false)}
                        onSave={handleSave}
                    />
                )}
            </div>
        </Layout>
    );
}
